'use client'

import * as React from 'react'
import { toast } from 'sonner'
import { cn } from '../../../lib/utils'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { Skeleton } from '../Skeleton'
import { Badge } from '../Badge'
import { Select } from '../Select'
import {
  Collapsible,
  CollapsibleContent,
} from '../Collapsible'
import { FieldMappingRow } from '../EntityCrmMapEditor/FieldMappingRow'
import type {
  CrmMapFieldMappingView,
  EntityCrmMapEditorLoadResult,
  EntityDefinitionView,
  EntityFieldDef,
  WorkplaceFieldOption,
} from '../EntityCrmMapEditor/types'
import type {
  CrmInstallGapAnalysis,
  CrmInstallSetupResult,
  EntityGapAnalysis,
  FieldNeedingDefault,
  FieldToCreate,
  FieldToMap,
} from '../CrmInstallSetupConsent'
import { Input } from '../Input'
import type { iconTypes } from '../Icon'
import { MigrationApprovalAlert } from '../MigrationApproval'

export type EventDirection = 'inbound' | 'outbound'

export type EntitySetupEventType = {
  type: string
  label: string
  description?: string
  icon?: iconTypes
  badge?: string
  /** Sync direction: 'inbound' (external → Skedyul) or 'outbound' (Skedyul → external) */
  direction?: EventDirection
}

export type EntitySetupWorkflowOption = {
  id: string
  name: string
  handle: string
  triggerId?: string | null
}

export type EntitySetupModelOption = {
  id: string
  name: string
  handle: string
}

/** null = create new model; string = workplace model id */
export type EntitySetupModelSelections = Record<string, string | null>

export type EntitySetupDialogMode = 'full' | 'connections' | 'liveUpdates'

export type ApplyConnectionsPendingMigrationImpact = {
  operationType: string
  resourceType: string
  resourceHandle: string
  affectedRecords?: number
  message?: string
  isDestructive: boolean
}

export type ApplyConnectionsPendingMigration = {
  migrationId: string
  impacts: ApplyConnectionsPendingMigrationImpact[]
  createdAt: string
  timeoutMinutes: number
}

export type ApplyConnectionsResult = {
  pendingMigrations?: ApplyConnectionsPendingMigration[]
}

export type EntitySetupMapSaveInput = {
  entityHandle: string
  targetModelId: string | null
  matchFieldId: string | null
  matchRules: Array<{
    entityPath: string
    targetFieldId: string
    sourceLiquid?: string
  }>
  fieldMappings: CrmMapFieldMappingView[]
  relationshipMappings: Array<{ handle: string; targetFieldId: string }>
}

export type { FieldNeedingDefault } from '../CrmInstallSetupConsent/types'

export type EntitySetupDialogProps = {
  /** When false, renders nothing (sidebar host unmounts instead). Defaults to true. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  title: string
  description?: string
  mode?: EntitySetupDialogMode
  entityHandles: string[]
  eventTypes: EntitySetupEventType[]
  recommendedWorkflowHandle?: string
  workflowHandles: string[]
  /** When false, only step/provision workflowHandles appear in selects */
  showOtherWorkflows?: boolean
  /** When true, show full field editor with direction controls after model selection */
  showFullFieldEditor?: boolean
  fetchModels: () => Promise<EntitySetupModelOption[]>
  fetchCrmSetup: (
    entityHandles: string[],
    modelSelections?: EntitySetupModelSelections,
  ) => Promise<CrmInstallSetupResult>
  applyConnections: (
    entityHandles: string[],
    modelSelections?: EntitySetupModelSelections,
    fieldDefaults?: Record<string, Record<string, string>>,
  ) => Promise<ApplyConnectionsResult | void>
  onApproveMigration?: (migrationId: string) => Promise<void>
  onDenyMigration?: (migrationId: string) => Promise<void>
  loadMap?: (entityHandle: string) => Promise<EntityCrmMapEditorLoadResult>
  saveMap?: (input: EntitySetupMapSaveInput) => Promise<void>
  fetchFields?: (modelId: string) => Promise<WorkplaceFieldOption[]>
  provisionWorkflows: (workflowHandles: string[]) => Promise<void>
  listCompatibleWorkflows: (
    eventType: string,
  ) => Promise<EntitySetupWorkflowOption[]>
  fetchLiveUpdatesOverview: (
    eventTypes: string[],
    recommendedWorkflowHandle?: string,
  ) => Promise<{
    events: Array<{
      eventType: string
      subscription: {
        id: string
        isEnabled: boolean
        triggerId: string
        workflow: { id: string; name: string; handle: string }
      } | null
    }>
    recommendedWorkflow?: {
      id: string
      name: string
      handle: string
    } | null
    availableWorkflows?: Array<{
      id: string
      name: string
      handle: string
      triggerId: string | null
      description?: string | null
      eventTypes?: string[]
      isEnabled?: boolean
    }>
  }>
  onFinish: (input: {
    selections: Record<string, string | null>
    enableLiveUpdates: boolean
    connectionsApplied: boolean
    pendingMigrationsResolved?: 'approved' | 'denied'
  }) => Promise<void>
  onManual?: () => void
  className?: string
}

const emptyAnalysis: CrmInstallGapAnalysis = {
  entities: [],
  summary: {
    modelsToCreate: 0,
    fieldsToCreate: 0,
    fieldsToMap: 0,
    fieldsNeedingDefault: 0,
    relationshipsToCreate: 0,
    relationshipsToMap: 0,
    hasGaps: false,
  },
}

const NONE_VALUE = '__none__'
const CREATE_NEW_VALUE = '__create_new__'

function summarizeConnections(analysis: CrmInstallGapAnalysis): string {
  const { summary } = analysis
  const parts: string[] = []
  if (summary.modelsToCreate > 0) {
    parts.push(
      `${summary.modelsToCreate} ${summary.modelsToCreate === 1 ? 'model' : 'models'}`,
    )
  }
  if (summary.fieldsToCreate > 0) {
    parts.push(
      `${summary.fieldsToCreate} ${summary.fieldsToCreate === 1 ? 'field' : 'fields'}`,
    )
  }
  const mapped = summary.fieldsToMap + summary.relationshipsToMap
  if (mapped > 0) {
    parts.push(`map ${mapped} ${mapped === 1 ? 'item' : 'items'}`)
  }
  const defaults = summary.fieldsNeedingDefault ?? 0
  if (defaults > 0) {
    parts.push(
      `${defaults} ${defaults === 1 ? 'default' : 'defaults'}`,
    )
  }
  if (parts.length === 0) return 'Connections look ready.'
  return `We’ll create or map ${parts.join(', ')}.`
}

function exactModelMatch(
  entity: Pick<EntityGapAnalysis, 'entityHandle' | 'entityLabel'>,
  models: EntitySetupModelOption[],
): EntitySetupModelOption | null {
  const candidates = [entity.entityHandle, entity.entityLabel]
    .filter(Boolean)
    .map((value) => value.trim().toLowerCase())

  return (
    models.find((model) => {
      const handles = [model.handle, model.name].map((value) =>
        value.trim().toLowerCase(),
      )
      return candidates.some((candidate) => handles.includes(candidate))
    }) ?? null
  )
}

function isAppWorkflowHandle(
  optionHandle: string,
  workflowHandles: string[],
): boolean {
  return workflowHandles.some(
    (handle) =>
      optionHandle === handle || optionHandle.endsWith(`/${handle}`),
  )
}

function appWorkflowSortIndex(
  optionHandle: string,
  workflowHandles: string[],
): number {
  const idx = workflowHandles.findIndex(
    (handle) =>
      optionHandle === handle || optionHandle.endsWith(`/${handle}`),
  )
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx
}

function pickDefaultWorkflowId(
  appOptions: EntitySetupWorkflowOption[],
  recommendedWorkflowHandle?: string,
): string | null {
  if (recommendedWorkflowHandle) {
    const recommended = appOptions.find(
      (opt) =>
        opt.handle === recommendedWorkflowHandle ||
        opt.handle.endsWith(`/${recommendedWorkflowHandle}`),
    )
    if (recommended) {
      return recommended.id
    }
  }
  return appOptions[0]?.id ?? null
}

function entityKeyFromEventType(eventType: string): string {
  const parts = eventType.split('.').filter(Boolean)
  if (parts[0] === 'app' && parts.length >= 4) {
    return parts[2]!
  }
  if (parts.length >= 2) {
    return parts[parts.length - 2]!
  }
  return parts[0] ?? eventType
}

function buildWorkflowSelectItems(
  options: EntitySetupWorkflowOption[],
  workflowHandles: string[],
  showOtherWorkflows: boolean,
  /** API already filtered by event type — do not hide bundled workflows missing from stale step config */
  typeMatchedFromApi = false,
) {
  const appOptions = (typeMatchedFromApi
    ? options
    : options.filter((opt) => isAppWorkflowHandle(opt.handle, workflowHandles))
  ).sort(
    (a, b) =>
      appWorkflowSortIndex(a.handle, workflowHandles) -
      appWorkflowSortIndex(b.handle, workflowHandles),
  )
  const otherOptions =
    showOtherWorkflows && !typeMatchedFromApi
      ? options.filter(
          (opt) => !isAppWorkflowHandle(opt.handle, workflowHandles),
        )
      : []

  const items: Array<{
    value: string | null
    label: string
    isDisabled?: boolean
  }> = [{ value: NONE_VALUE, label: 'Don’t subscribe' }]

  if (appOptions.length > 0) {
    items.push({
      value: '__group_app__',
      label: 'App workflows',
      isDisabled: true,
    })
    for (const opt of appOptions) {
      items.push({ value: opt.id, label: opt.name })
    }
  }

  if (otherOptions.length > 0) {
    items.push({
      value: '__group_other__',
      label: 'Other workflows',
      isDisabled: true,
    })
    for (const opt of otherOptions) {
      items.push({ value: opt.id, label: opt.name })
    }
  }

  return { items, appOptions }
}

function finishLabel(
  mode: EntitySetupDialogMode,
  isFinishing: boolean,
): string {
  if (isFinishing) return 'Saving…'
  if (mode === 'connections') return 'Save connections'
  if (mode === 'liveUpdates') return 'Save live updates'
  return 'Save'
}

function entityGroupLabel(entityKey: string): string {
  if (!entityKey) return 'Events'
  return entityKey
    .split(/[_-]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function DirectionSectionHeader({
  direction,
  description,
}: {
  direction: EventDirection
  description?: string
}) {
  const isInbound = direction === 'inbound'
  const defaultDescription = isInbound
    ? 'When something changes externally'
    : 'When something is saved in Skedyul'

  return (
    <div className="flex items-center gap-2 pb-1 pt-2">
      <Icon
        name={isInbound ? 'ArrowDown' : 'ArrowUp'}
        size={12}
        className={cn(
          'shrink-0',
          isInbound ? 'text-blue-500' : 'text-emerald-500',
        )}
      />
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {isInbound ? 'Incoming' : 'Outgoing'}
        </p>
        <p className="text-[9px] text-muted-foreground/70">
          {description ?? defaultDescription}
        </p>
      </div>
    </div>
  )
}

type EventGroupByEntity = {
  entityKey: string
  label: string
  events: EntitySetupEventType[]
  inboundEvents: EntitySetupEventType[]
  outboundEvents: EntitySetupEventType[]
  hasDirectionInfo: boolean
}

function groupEventsByEntity(eventTypes: EntitySetupEventType[]): EventGroupByEntity[] {
  const groups: EventGroupByEntity[] = []
  const indexByKey = new Map<string, number>()

  for (const event of eventTypes) {
    const entityKey = entityKeyFromEventType(event.type)
    const existing = indexByKey.get(entityKey)
    if (existing == null) {
      indexByKey.set(entityKey, groups.length)
      groups.push({
        entityKey,
        label: entityGroupLabel(entityKey),
        events: [event],
        inboundEvents: event.direction === 'outbound' ? [] : [event],
        outboundEvents: event.direction === 'outbound' ? [event] : [],
        hasDirectionInfo: Boolean(event.direction),
      })
    } else {
      const group = groups[existing]!
      group.events.push(event)
      if (event.direction === 'outbound') {
        group.outboundEvents.push(event)
      } else {
        group.inboundEvents.push(event)
      }
      if (event.direction) {
        group.hasDirectionInfo = true
      }
    }
  }

  return groups
}

type FieldChipTarget =
  | { kind: 'map'; field: FieldToMap }
  | { kind: 'create'; field: FieldToCreate }

function FullFieldMappingPanel({
  entityHandle,
  entityLabel,
  isLoading,
  entityDef,
  targetModelId,
  fieldMappings,
  fields,
  onMappingChange,
  onSave,
  isSaving,
}: {
  entityHandle: string
  entityLabel: string
  isLoading: boolean
  entityDef: EntityDefinitionView | null
  targetModelId: string | null
  fieldMappings: Record<string, CrmMapFieldMappingView>
  fields: WorkplaceFieldOption[]
  onMappingChange: (entityPath: string, mapping: CrmMapFieldMappingView | null) => void
  onSave: () => Promise<void>
  isSaving: boolean
}) {
  const allFields = React.useMemo(() => {
    if (!entityDef) return []
    return [
      ...entityDef.fields,
      ...(entityDef.contextFields ?? []).map((f: EntityFieldDef) => ({
        ...f,
        isContext: true,
      })),
    ]
  }, [entityDef])

  const contextFields = React.useMemo(() => {
    if (!entityDef) return []
    return [...entityDef.fields, ...(entityDef.contextFields ?? [])].map(
      (field) => ({
        path: field.handle,
        label: field.label,
        type: field.type,
      }),
    )
  }, [entityDef])

  if (isLoading) {
    return (
      <div className="space-y-2 rounded-lg border bg-muted/10 p-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (!entityDef || !targetModelId) {
    return null
  }

  return (
    <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Field Mappings</p>
          <p className="text-xs text-muted-foreground">
            {allFields.length} fields • Configure direction per field
          </p>
        </div>
        <Button
          size="sm"
          variant="default"
          leftIcon={isSaving ? undefined : 'Save'}
          label={isSaving ? 'Saving...' : 'Save'}
          isDisabled={isSaving}
          isLoading={isSaving}
          handleClick={() => void onSave()}
        />
      </div>
      <div className="max-h-[400px] space-y-1 overflow-y-auto">
        {allFields.map((field) => {
          const mapping = fieldMappings[field.handle]
          const targetField = mapping?.targetFieldId
            ? fields.find((f) => f.id === mapping.targetFieldId)
            : undefined
          return (
            <FieldMappingRow
              key={field.handle}
              field={field}
              mapping={mapping}
              fields={fields}
              targetField={targetField}
              contextFields={contextFields}
              showDirectionControls
              onChange={(nextMapping) =>
                onMappingChange(field.handle, nextMapping)
              }
            />
          )
        })}
      </div>
    </div>
  )
}

function FieldEditorInlinePanel({
  isLoading,
  editorField,
  editorMapping,
  editorFields,
  targetField,
  editorContextFields,
  onMappingChange,
}: {
  isLoading: boolean
  editorField: EntityFieldDef | null
  editorMapping: CrmMapFieldMappingView | null
  editorFields: WorkplaceFieldOption[]
  targetField?: WorkplaceFieldOption
  editorContextFields: Array<{
    path: string
    label: string
    type?: string
  }>
  onMappingChange: (mapping: CrmMapFieldMappingView | null) => void
}) {
  return (
    <div className="rounded-lg border border-foreground/15 bg-card p-2.5 shadow-sm">
      {isLoading || !editorField ? (
        <Skeleton className="h-10 w-full rounded-lg" />
      ) : (
        <FieldMappingRow
          field={editorField}
          mapping={editorMapping ?? undefined}
          fields={editorFields}
          targetField={targetField}
          contextFields={editorContextFields}
          onChange={onMappingChange}
          forceValueMapOpen
          hideFieldLabel
          showDirectionControls
        />
      )}
    </div>
  )
}

function FieldChipExpandable({
  label,
  isCreate,
  isExpanded,
  canEdit,
  onToggle,
  children,
  hasValueMap,
  valueMapCount,
}: {
  label: string
  isCreate: boolean
  isExpanded: boolean
  canEdit: boolean
  onToggle: () => void
  children?: React.ReactNode
  /** Show enum/valueMap indicator */
  hasValueMap?: boolean
  /** Number of mapped values */
  valueMapCount?: number
}) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isExpanded) return
    containerRef.current?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [isExpanded])

  return (
    <div
      ref={containerRef}
      className={cn(
        'min-w-0 transition-[flex-basis,width] duration-200 ease-out',
        isExpanded ? 'w-full basis-full' : 'basis-auto',
      )}
    >
      <button
        type="button"
        disabled={!canEdit}
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={cn(
          'inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition-all duration-200',
          isCreate
            ? 'border-dashed border-border/70 bg-muted/20 text-muted-foreground'
            : 'border-border/70 bg-background text-muted-foreground',
          isExpanded &&
            'border-foreground/35 bg-muted/40 text-foreground shadow-sm ring-2 ring-ring/20',
          canEdit &&
            !isExpanded &&
            'hover:border-foreground/30 hover:text-foreground',
          !canEdit && 'opacity-60',
        )}
      >
        <span className="truncate">{label}</span>
        {hasValueMap && valueMapCount != null && valueMapCount > 0 && !isExpanded && (
          <span className="shrink-0 rounded bg-primary/10 px-1 py-0.5 text-[9px] font-medium text-primary">
            {valueMapCount} mapped
          </span>
        )}
        {isExpanded ? (
          <Icon name="ChevronUp" className="h-3 w-3 shrink-0 opacity-70" />
        ) : (
          <Icon name="ChevronDown" className="h-3 w-3 shrink-0 opacity-40" />
        )}
      </button>
      <Collapsible open={isExpanded}>
        <CollapsibleContent className="pt-2">{children}</CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function DefaultFieldChip({
  field,
  value,
  canEdit,
  onChange,
}: {
  field: FieldNeedingDefault
  value: string
  canEdit: boolean
  onChange: (value: string) => void
}) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const hasValue = value.trim() !== ''
  const optionItems =
    field.options?.map((o) => ({ value: o.value, label: o.label })) ?? []

  return (
    <div
      className={cn(
        'min-w-0 transition-[flex-basis,width] duration-200 ease-out',
        isExpanded ? 'w-full basis-full' : 'basis-auto',
      )}
    >
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => setIsExpanded((v) => !v)}
        className={cn(
          'inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition-all duration-200',
          hasValue
            ? 'border-border/70 bg-background text-muted-foreground'
            : 'border-dashed border-amber-500/40 bg-amber-500/5 text-amber-800/80 dark:text-amber-200/80',
          isExpanded &&
            'border-foreground/35 bg-muted/40 text-foreground shadow-sm ring-2 ring-ring/20',
          !isExpanded && canEdit && 'hover:border-foreground/30 hover:text-foreground',
        )}
      >
        <span className="truncate">
          {field.label}
          {hasValue && <span className="ml-1 opacity-60">= {value}</span>}
        </span>
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          className="h-3 w-3 shrink-0 opacity-40"
        />
      </button>
      <Collapsible open={isExpanded}>
        <CollapsibleContent className="pt-2">
          <div className="space-y-1.5 rounded-lg border border-foreground/15 bg-card p-2.5 shadow-sm">
            <p className="text-[10px] text-muted-foreground">
              {field.requirement === 'REQUIRED'
                ? 'Required field'
                : 'Required on create'}{' '}
              – no entity mapping. Set a default (or liquid via field editor after save).
            </p>
            {optionItems.length > 0 ? (
              <Select
                id={`crm-default-${field.targetFieldId}`}
                placeholder="Choose default…"
                value={value || undefined}
                items={optionItems}
                handleChange={(next) => onChange(next)}
              />
            ) : (
              <Input
                id={`crm-default-${field.targetFieldId}`}
                type="text"
                placeholder="Default value…"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-7 text-[11px]"
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function FieldGapChips({
  entity,
  canEdit,
  expandedEntityPath,
  expandedEditor,
  onOpenField,
  defaultValues,
  onDefaultChange,
}: {
  entity: EntityGapAnalysis
  canEdit: boolean
  expandedEntityPath: string | null
  expandedEditor: React.ReactNode | null
  onOpenField: (target: FieldChipTarget) => void
  defaultValues: Record<string, string>
  onDefaultChange: (targetFieldId: string, value: string) => void
}) {
  const relMap = entity.relationshipsToMap.map((rel) => rel.label)
  const relCreate = entity.relationshipsToCreate.map((rel) => rel.label)
  const needingDefaults = entity.fieldsNeedingDefault ?? []

  if (
    entity.fieldsToMap.length === 0 &&
    entity.fieldsToCreate.length === 0 &&
    needingDefaults.length === 0 &&
    relMap.length === 0 &&
    relCreate.length === 0
  ) {
    return (
      <p className="text-[11px] text-muted-foreground/80">
        Nothing to change for this connection.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {entity.fieldsToMap.length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
            Reuse
          </p>
          <div className="flex flex-wrap gap-1.5">
            {entity.fieldsToMap.map((field) => {
              const isExpanded = expandedEntityPath === field.entityPath
              const valueMapCount = field.suggestedValueMap
                ? Object.keys(field.suggestedValueMap).length
                : 0
              return (
                <FieldChipExpandable
                  key={`map-${field.entityPath}`}
                  label={field.label}
                  isCreate={false}
                  isExpanded={isExpanded}
                  canEdit={canEdit}
                  onToggle={() => onOpenField({ kind: 'map', field })}
                  hasValueMap={valueMapCount > 0}
                  valueMapCount={valueMapCount}
                >
                  {isExpanded ? expandedEditor : null}
                </FieldChipExpandable>
              )
            })}
          </div>
        </div>
      )}
      {(entity.fieldsToCreate.length > 0 || needingDefaults.length > 0) && (
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
            Create
          </p>
          <div className="flex flex-wrap gap-1.5">
            {entity.fieldsToCreate.map((field) => {
              const isExpanded = expandedEntityPath === field.entityPath
              return (
                <FieldChipExpandable
                  key={`create-${field.entityPath}`}
                  label={field.label}
                  isCreate
                  isExpanded={isExpanded}
                  canEdit={canEdit}
                  onToggle={() => onOpenField({ kind: 'create', field })}
                >
                  {isExpanded ? expandedEditor : null}
                </FieldChipExpandable>
              )
            })}
            {/* Special required: assigned CRM defaults stay visible after Save */}
            {needingDefaults.map((field) => (
              <DefaultFieldChip
                key={`default-${field.targetFieldId}`}
                field={field}
                value={
                  defaultValues[field.targetFieldId] ??
                  (field.defaultValue != null
                    ? String(field.defaultValue)
                    : field.suggestedDefaultValue != null
                      ? String(field.suggestedDefaultValue)
                      : '')
                }
                canEdit={canEdit}
                onChange={(value) =>
                  onDefaultChange(field.targetFieldId, value)
                }
              />
            ))}
          </div>
        </div>
      )}
      {(relMap.length > 0 || relCreate.length > 0) && (
        <div className="space-y-0.5 text-[11px] leading-relaxed text-muted-foreground/80">
          {relMap.length > 0 && (
            <p>
              <span className="text-muted-foreground">Reuse relations</span>{' '}
              {relMap.join(', ')}
            </p>
          )}
          {relCreate.length > 0 && (
            <p>
              <span className="text-muted-foreground">Create relations</span>{' '}
              {relCreate.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function EntityConnectionRow({
  entity,
  models,
  selectedModelId,
  takenModelIds,
  canEditFields,
  expandedEntityPath,
  expandedEditor,
  onSelectModel,
  onOpenField,
  defaultValues,
  onDefaultChange,
}: {
  entity: EntityGapAnalysis
  models: EntitySetupModelOption[]
  selectedModelId: string | null
  takenModelIds: Set<string>
  canEditFields: boolean
  expandedEntityPath: string | null
  expandedEditor: React.ReactNode | null
  onSelectModel: (modelId: string | null) => void
  onOpenField: (target: FieldChipTarget) => void
  defaultValues: Record<string, string>
  onDefaultChange: (targetFieldId: string, value: string) => void
}) {
  const selectValue =
    selectedModelId == null ? CREATE_NEW_VALUE : selectedModelId

  const items = [
    { value: CREATE_NEW_VALUE, label: 'Create New Model' },
    ...models
      .filter(
        (model) =>
          model.id === selectedModelId || !takenModelIds.has(model.id),
      )
      .map((model) => ({
        value: model.id,
        label: model.name,
        description: model.handle,
      })),
  ]

  return (
    <div className="space-y-2 rounded-lg border bg-background/60 px-3 py-3">
      <div className="flex items-start gap-2">
        <Icon
          name={
            !entity.missingModel &&
            entity.fieldsToCreate.length === 0 &&
            entity.relationshipsToCreate.length === 0
              ? 'Check'
              : 'Circle'
          }
          size={16}
          className={
            !entity.missingModel &&
            entity.fieldsToCreate.length === 0 &&
            entity.relationshipsToCreate.length === 0
              ? 'mt-0.5 text-emerald-600'
              : 'mt-0.5 text-muted-foreground'
          }
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <p className="truncate text-sm font-medium">{entity.entityLabel}</p>
            <p className="truncate text-xs text-muted-foreground">
              {entity.missingModel
                ? 'Will create a new CRM model'
                : `Maps to ${entity.suggestedModelName ?? entity.suggestedModelHandle}`}
            </p>
          </div>
          <Select
            id={`entity-setup-model-${entity.entityHandle}`}
            placeholder="Choose CRM model"
            value={selectValue}
            items={items}
            handleChange={(next) =>
              onSelectModel(next === CREATE_NEW_VALUE ? null : next)
            }
          />
          <FieldGapChips
            entity={entity}
            canEdit={canEditFields}
            expandedEntityPath={expandedEntityPath}
            expandedEditor={expandedEditor}
            onOpenField={onOpenField}
            defaultValues={defaultValues}
            onDefaultChange={onDefaultChange}
          />
        </div>
      </div>
    </div>
  )
}

function normalizeEntityFieldOptions(
  field: EntityFieldDef | undefined,
): EntityFieldDef['options'] {
  if (!field?.options?.length) return field?.options
  return field.options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  )
}

export const EntitySetupDialog: React.FC<EntitySetupDialogProps> = ({
  open = true,
  onOpenChange,
  title,
  description,
  mode = 'full',
  entityHandles,
  eventTypes,
  recommendedWorkflowHandle,
  workflowHandles,
  showOtherWorkflows = true,
  showFullFieldEditor = false,
  fetchModels,
  fetchCrmSetup,
  applyConnections,
  onApproveMigration,
  onDenyMigration,
  loadMap,
  saveMap,
  fetchFields,
  provisionWorkflows,
  listCompatibleWorkflows,
  fetchLiveUpdatesOverview,
  onFinish,
  onManual,
  className,
}) => {
  const showConnections =
    (mode === 'full' || mode === 'connections') && entityHandles.length > 0
  const requireLiveUpdates = workflowHandles.length > 0
  const showLiveUpdates =
    (mode === 'full' || mode === 'liveUpdates') &&
    (eventTypes.length > 0 || requireLiveUpdates)

  const [analysis, setAnalysis] =
    React.useState<CrmInstallGapAnalysis>(emptyAnalysis)
  const [models, setModels] = React.useState<EntitySetupModelOption[]>([])
  const [modelSelections, setModelSelections] =
    React.useState<EntitySetupModelSelections>({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [isRefreshingConnections, setIsRefreshingConnections] =
    React.useState(false)
  const [isFinishing, setIsFinishing] = React.useState(false)
  const [enableLiveUpdates, setEnableLiveUpdates] = React.useState(true)
  const [selections, setSelections] = React.useState<
    Record<string, string | null>
  >({})
  const [workflowOptions, setWorkflowOptions] = React.useState<
    Record<string, EntitySetupWorkflowOption[]>
  >({})
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const hasInitializedSelections = React.useRef(false)

  const [fieldEditor, setFieldEditor] = React.useState<{
    entityHandle: string
    entityPath: string
    label: string
  } | null>(null)
  const [fieldEditorLoading, setFieldEditorLoading] = React.useState(false)
  const [editorField, setEditorField] = React.useState<EntityFieldDef | null>(
    null,
  )
  const [editorMapping, setEditorMapping] =
    React.useState<CrmMapFieldMappingView | null>(null)
  const [editorFields, setEditorFields] = React.useState<
    WorkplaceFieldOption[]
  >([])
  const [editorMapSnapshot, setEditorMapSnapshot] =
    React.useState<EntityCrmMapEditorLoadResult | null>(null)
  const [stagedFieldMappings, setStagedFieldMappings] = React.useState<
    Record<string, Record<string, CrmMapFieldMappingView | null>>
  >({})
  // Full field editor state
  const [fullFieldEditorData, setFullFieldEditorData] = React.useState<
    Record<string, {
      entityDef: EntityDefinitionView | null
      fieldMappings: Record<string, CrmMapFieldMappingView>
      fields: WorkplaceFieldOption[]
    }>
  >({})
  const [fullFieldEditorLoading, setFullFieldEditorLoading] = React.useState<
    Record<string, boolean>
  >({})
  const [fullFieldEditorSaving, setFullFieldEditorSaving] = React.useState<
    Record<string, boolean>
  >({})
  const [pendingMigrations, setPendingMigrations] = React.useState<
    ApplyConnectionsPendingMigration[]
  >([])
  const [finishPausedForApproval, setFinishPausedForApproval] =
    React.useState(false)
  const [isResolvingMigration, setIsResolvingMigration] = React.useState(false)
  const deniedDuringApprovalRef = React.useRef(false)

  const mergePendingMigrations = (
    incoming: ApplyConnectionsPendingMigration[] | undefined,
  ) => {
    if (!incoming?.length) return incoming ?? []
    setPendingMigrations((prev) => {
      const byId = new Map(prev.map((item) => [item.migrationId, item]))
      for (const item of incoming) byId.set(item.migrationId, item)
      return [...byId.values()]
    })
    return incoming
  }

  /** entityHandle → targetFieldId → default value for CRM Defaults */
  const [stagedDefaults, setStagedDefaults] = React.useState<
    Record<string, Record<string, string>>
  >({})

  const takenModelIds = React.useMemo(() => {
    const taken = new Set<string>()
    for (const modelId of Object.values(modelSelections)) {
      if (modelId) taken.add(modelId)
    }
    return taken
  }, [modelSelections])

  const stageFieldMapping = React.useCallback(
    (
      entityHandle: string,
      entityPath: string,
      mapping: CrmMapFieldMappingView | null,
    ) => {
      setStagedFieldMappings((prev) => ({
        ...prev,
        [entityHandle]: {
          ...(prev[entityHandle] ?? {}),
          [entityPath]: mapping,
        },
      }))
    },
    [],
  )

  const handleEditorMappingChange = (
    mapping: CrmMapFieldMappingView | null,
  ) => {
    setEditorMapping(mapping)
    if (fieldEditor) {
      stageFieldMapping(
        fieldEditor.entityHandle,
        fieldEditor.entityPath,
        mapping,
      )
    }
  }

  const canEditFields = Boolean(loadMap && saveMap && fetchFields)
  const eventGroups = React.useMemo(
    () => groupEventsByEntity(eventTypes),
    [eventTypes],
  )

  const refreshAnalysis = React.useCallback(
    async (nextSelections: EntitySetupModelSelections) => {
      if (!showConnections || entityHandles.length === 0) {
        setAnalysis(emptyAnalysis)
        return emptyAnalysis
      }
      const crm = await fetchCrmSetup(entityHandles, nextSelections)
      setAnalysis(crm.analysis)
      // Prefill Defaults from analysis suggestions (keep user edits for same fields)
      setStagedDefaults((prev) => {
        const next: Record<string, Record<string, string>> = { ...prev }
        for (const entity of crm.analysis.entities) {
          const entityDefaults = { ...(next[entity.entityHandle] ?? {}) }
          for (const field of entity.fieldsNeedingDefault ?? []) {
            if (entityDefaults[field.targetFieldId] != null) continue
            const seed =
              field.defaultValue != null
                ? String(field.defaultValue)
                : field.suggestedDefaultValue != null
                  ? String(field.suggestedDefaultValue)
                  : ''
            if (seed) entityDefaults[field.targetFieldId] = seed
          }
          next[entity.entityHandle] = entityDefaults
        }
        return next
      })
      return crm.analysis
    },
    [entityHandles, fetchCrmSetup, showConnections],
  )

  const load = React.useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    hasInitializedSelections.current = false
    setStagedFieldMappings({})
    setStagedDefaults({})
    try {
      const modelPromise = showConnections
        ? fetchModels()
        : Promise.resolve([] as EntitySetupModelOption[])
      const crmPromise =
        showConnections && entityHandles.length > 0
          ? fetchCrmSetup(entityHandles)
          : Promise.resolve({
              crmSetupStatus: 'PENDING' as const,
              analysis: emptyAnalysis,
            })
      const overviewPromise =
        showLiveUpdates && eventTypes.length > 0
          ? fetchLiveUpdatesOverview(
              eventTypes.map((e) => e.type),
              recommendedWorkflowHandle,
            )
          : Promise.resolve({ events: [], recommendedWorkflow: null })

      const [modelRows, seedCrm, overview] = await Promise.all([
        modelPromise,
        crmPromise,
        overviewPromise,
      ])
      setModels(modelRows)

      const defaults: EntitySetupModelSelections = {}
      if (showConnections) {
        for (const entity of seedCrm.analysis.entities) {
          // Prefer server suggestion (includes existing AppCrmMap remaps like
          // member → prospect), then exact handle/name match, else Create New.
          const exact = exactModelMatch(entity, modelRows)
          const suggestedId = entity.suggestedModelId
          const suggestedStillExists =
            suggestedId != null &&
            modelRows.some((model) => model.id === suggestedId)
          defaults[entity.entityHandle] = suggestedStillExists
            ? suggestedId
            : (exact?.id ?? null)
        }
        for (const handle of entityHandles) {
          if (!(handle in defaults)) defaults[handle] = null
        }
        setModelSelections(defaults)
        await refreshAnalysis(defaults)
      } else {
        setModelSelections({})
        setAnalysis(emptyAnalysis)
      }
      hasInitializedSelections.current = true

      const nextSelections: Record<string, string | null> = {}
      const nextOptions: Record<string, EntitySetupWorkflowOption[]> = {}

      if (showLiveUpdates) {
        await Promise.all(
          eventTypes.map(async (event) => {
            const options = await listCompatibleWorkflows(event.type)
            nextOptions[event.type] = options
            const { appOptions } = buildWorkflowSelectItems(
              options,
              workflowHandles,
              showOtherWorkflows,
              true,
            )
            const existing = overview.events.find(
              (row) => row.eventType === event.type,
            )?.subscription
            if (existing) {
              nextSelections[event.type] = existing.workflow.id
              return
            }
            nextSelections[event.type] = pickDefaultWorkflowId(
              appOptions,
              recommendedWorkflowHandle,
            )
          }),
        )
      }

      setWorkflowOptions(nextOptions)
      setSelections(nextSelections)
    } catch (err) {
      console.error('Failed to load entity setup', err)
      setLoadError('Unable to load setup details')
    } finally {
      setIsLoading(false)
    }
  }, [
    entityHandles,
    eventTypes,
    fetchCrmSetup,
    fetchLiveUpdatesOverview,
    fetchModels,
    listCompatibleWorkflows,
    recommendedWorkflowHandle,
    refreshAnalysis,
    showConnections,
    showLiveUpdates,
    showOtherWorkflows,
    workflowHandles,
  ])

  React.useEffect(() => {
    if (!open) return
    void load()
  }, [open, load])
  
  // Load full field editor data when showFullFieldEditor is enabled and model selections change
  React.useEffect(() => {
    if (!showFullFieldEditor || !open) return
    
    for (const [entityHandle, modelId] of Object.entries(modelSelections)) {
      if (modelId && !fullFieldEditorData[entityHandle] && !fullFieldEditorLoading[entityHandle]) {
        void loadFullFieldEditorData(entityHandle, modelId)
      }
    }
  }, [showFullFieldEditor, modelSelections, open])

  const close = () => onOpenChange?.(false)

  const loadFullFieldEditorData = async (
    entityHandle: string,
    modelId: string,
  ) => {
    if (!loadMap || !fetchFields) return
    
    setFullFieldEditorLoading((prev) => ({ ...prev, [entityHandle]: true }))
    try {
      const [mapResult, fieldsResult] = await Promise.all([
        loadMap(entityHandle),
        fetchFields(modelId),
      ])
      
      const mappings: Record<string, CrmMapFieldMappingView> = {}
      for (const fm of mapResult.map?.fieldMappings ?? []) {
        mappings[fm.entityPath] = fm
      }
      
      setFullFieldEditorData((prev) => ({
        ...prev,
        [entityHandle]: {
          entityDef: mapResult.entity,
          fieldMappings: mappings,
          fields: fieldsResult,
        },
      }))
    } catch (err) {
      console.error('Failed to load full field editor data', err)
      toast.error('Unable to load field mappings')
    } finally {
      setFullFieldEditorLoading((prev) => ({ ...prev, [entityHandle]: false }))
    }
  }
  
  const updateFullFieldMapping = (
    entityHandle: string,
    entityPath: string,
    mapping: CrmMapFieldMappingView | null,
  ) => {
    setFullFieldEditorData((prev) => {
      const existing = prev[entityHandle]
      if (!existing) return prev
      
      const nextMappings = { ...existing.fieldMappings }
      if (mapping) {
        nextMappings[entityPath] = mapping
      } else {
        delete nextMappings[entityPath]
      }
      
      return {
        ...prev,
        [entityHandle]: {
          ...existing,
          fieldMappings: nextMappings,
        },
      }
    })
  }
  
  const saveFullFieldEditor = async (entityHandle: string) => {
    if (!saveMap || !loadMap) return
    
    const data = fullFieldEditorData[entityHandle]
    const modelId = modelSelections[entityHandle]
    if (!data || !modelId) return
    
    setFullFieldEditorSaving((prev) => ({ ...prev, [entityHandle]: true }))
    try {
      // Load current map to get match rules and relationship mappings
      const currentMap = await loadMap(entityHandle)
      const baseMap = currentMap.map ?? {
        entityHandle,
        targetModelId: modelId,
        matchFieldId: null,
        matchRules: [],
        fieldMappings: [],
        relationshipMappings: [],
      }
      
      await saveMap({
        entityHandle,
        targetModelId: modelId,
        matchFieldId: baseMap.matchFieldId,
        matchRules: baseMap.matchRules,
        fieldMappings: Object.values(data.fieldMappings).filter(
          (m): m is CrmMapFieldMappingView => Boolean(m?.targetFieldId),
        ),
        relationshipMappings: baseMap.relationshipMappings,
      })
      
      toast.success('Field mappings saved')
    } catch (err) {
      console.error('Failed to save field mappings', err)
      toast.error('Unable to save field mappings')
    } finally {
      setFullFieldEditorSaving((prev) => ({ ...prev, [entityHandle]: false }))
    }
  }
  
  const handleModelChange = async (
    entityHandle: string,
    modelId: string | null,
  ) => {
    const next = { ...modelSelections, [entityHandle]: modelId }
    setModelSelections(next)
    if (!hasInitializedSelections.current) return
    setIsRefreshingConnections(true)
    try {
      await refreshAnalysis(next)
      // Load full field editor data if enabled
      if (showFullFieldEditor && modelId) {
        await loadFullFieldEditorData(entityHandle, modelId)
      }
    } catch (err) {
      console.error('Failed to refresh CRM setup analysis', err)
      toast.error('Unable to update connection preview')
    } finally {
      setIsRefreshingConnections(false)
    }
  }

  const closeFieldEditor = () => {
    setFieldEditor(null)
    setEditorField(null)
    setEditorMapping(null)
    setEditorFields([])
    setEditorMapSnapshot(null)
  }

  const openFieldEditor = async (
    entity: EntityGapAnalysis,
    target: FieldChipTarget,
  ) => {
    if (!loadMap || !saveMap || !fetchFields) return

    const entityPath = target.field.entityPath
    if (
      fieldEditor?.entityHandle === entity.entityHandle &&
      fieldEditor.entityPath === entityPath
    ) {
      closeFieldEditor()
      return
    }

    const label = target.field.label

    setFieldEditor({
      entityHandle: entity.entityHandle,
      entityPath,
      label,
    })
    setFieldEditorLoading(true)
    setEditorField(null)
    setEditorMapping(null)
    setEditorFields([])
    setEditorMapSnapshot(null)

    try {
      const applyResult = await applyConnections(
        [entity.entityHandle],
        modelSelections,
        stagedDefaults,
      )
      mergePendingMigrations(applyResult?.pendingMigrations)
      await refreshAnalysis(modelSelections)

      const loaded = await loadMap(entity.entityHandle)
      setEditorMapSnapshot(loaded)

      const modelId =
        modelSelections[entity.entityHandle] ??
        loaded.map?.targetModelId ??
        loaded.suggestedDefaults?.targetModelId ??
        null
      if (!modelId) {
        throw new Error('Select a CRM model before mapping fields')
      }

      const workplaceFields = await fetchFields(modelId)
      setEditorFields(workplaceFields)

      const entityDef = loaded.entity
      const rawField =
        entityDef?.fields.find((f) => f.handle === entityPath) ??
        entityDef?.contextFields?.find((f) => f.handle === entityPath)

      const fieldDef: EntityFieldDef = {
        handle: entityPath,
        label: rawField?.label ?? label,
        type:
          rawField?.type ??
          (target.kind === 'create' ? target.field.type : undefined),
        required:
          rawField?.required ??
          (target.kind === 'create' ? target.field.required : undefined),
        options: normalizeEntityFieldOptions(rawField),
      }

      setEditorField(fieldDef)

      const existing =
        stagedFieldMappings[entity.entityHandle]?.[entityPath] ??
        loaded.map?.fieldMappings.find((m) => m.entityPath === entityPath) ??
        loaded.suggestedDefaults?.fieldMappings.find(
          (m) => m.entityPath === entityPath,
        ) ??
        null
      setEditorMapping(existing ?? null)
    } catch (err) {
      console.error('Failed to open field editor', err)
      toast.error(
        err instanceof Error ? err.message : 'Unable to open field mapping',
      )
      closeFieldEditor()
    } finally {
      setFieldEditorLoading(false)
    }
  }

  const persistStagedFieldMappings = async () => {
    if (!saveMap || !loadMap) return

    const mappingsToPersist: Record<
      string,
      Record<string, CrmMapFieldMappingView | null>
    > = { ...stagedFieldMappings }

    if (fieldEditor) {
      mappingsToPersist[fieldEditor.entityHandle] = {
        ...(mappingsToPersist[fieldEditor.entityHandle] ?? {}),
        [fieldEditor.entityPath]: editorMapping,
      }
    }

    for (const [entityHandle, pathMappings] of Object.entries(
      mappingsToPersist,
    )) {
      if (Object.keys(pathMappings).length === 0) continue

      const loaded = await loadMap(entityHandle)
      const baseMap =
        loaded.map ??
        loaded.suggestedDefaults ??
        ({
          entityHandle,
          targetModelId: modelSelections[entityHandle] ?? null,
          matchFieldId: null,
          matchRules: [],
          fieldMappings: [],
          relationshipMappings: [],
        } satisfies EntitySetupMapSaveInput)

      let nextMappings = [...baseMap.fieldMappings]
      for (const [entityPath, mapping] of Object.entries(pathMappings)) {
        nextMappings = nextMappings.filter((m) => m.entityPath !== entityPath)
        if (mapping) nextMappings.push(mapping)
      }

      await saveMap({
        entityHandle,
        targetModelId:
          modelSelections[entityHandle] ?? baseMap.targetModelId ?? null,
        matchFieldId: baseMap.matchFieldId,
        matchRules: baseMap.matchRules,
        fieldMappings: nextMappings,
        relationshipMappings: baseMap.relationshipMappings,
      })
    }

    if (Object.keys(mappingsToPersist).length > 0) {
      setStagedFieldMappings({})
      await refreshAnalysis(modelSelections)
    }
  }

  const defaultsReady = analysis.entities.every((entity) =>
    (entity.fieldsNeedingDefault ?? []).every((field) => {
      const staged =
        stagedDefaults[entity.entityHandle]?.[field.targetFieldId]
      if (staged != null && staged.trim() !== '') return true
      const seed = field.defaultValue ?? field.suggestedDefaultValue
      return seed !== undefined && seed !== null && seed !== ''
    }),
  )
  const connectionsReady =
    analysis.summary.modelsToCreate === 0 &&
    analysis.summary.fieldsToCreate === 0 &&
    analysis.summary.relationshipsToCreate === 0 &&
    defaultsReady
  const targetField = editorFields.find(
    (field) => field.id === editorMapping?.targetFieldId,
  )
  const editorContextFields = React.useMemo(() => {
    const entityDef = editorMapSnapshot?.entity
    if (!entityDef) {
      return editorField
        ? [
            {
              path: editorField.handle,
              label: editorField.label,
              type: editorField.type,
            },
          ]
        : []
    }
    return [...entityDef.fields, ...(entityDef.contextFields ?? [])].map(
      (field) => ({
        path: field.handle,
        label: field.label,
        type: field.type,
      }),
    )
  }, [editorField, editorMapSnapshot?.entity])

  const fieldEditorPanel = (
    <FieldEditorInlinePanel
      isLoading={fieldEditorLoading}
      editorField={editorField}
      editorMapping={editorMapping}
      editorFields={editorFields}
      targetField={targetField}
      editorContextFields={editorContextFields}
      onMappingChange={handleEditorMappingChange}
    />
  )

  const completeFinish = async (
    connectionsApplied: boolean,
    pendingMigrationsResolved?: 'approved' | 'denied',
  ) => {
    const shouldProvisionLiveUpdates =
      requireLiveUpdates || (showLiveUpdates && enableLiveUpdates)

    if (
      pendingMigrationsResolved !== 'denied' &&
      shouldProvisionLiveUpdates &&
      workflowHandles.length > 0
    ) {
      // Blocking: CRM setup steps require these Triggers before marking READY.
      await provisionWorkflows(workflowHandles)
    }

    await onFinish({
      selections: shouldProvisionLiveUpdates ? selections : {},
      enableLiveUpdates: shouldProvisionLiveUpdates,
      connectionsApplied,
      pendingMigrationsResolved,
    })
    if (pendingMigrationsResolved !== 'denied') {
      close()
    }
  }

  const handleApproveMigration = async (migrationId: string) => {
    if (!onApproveMigration) {
      toast.error('Approval is unavailable')
      return
    }
    setIsResolvingMigration(true)
    try {
      await onApproveMigration(migrationId)
      const remaining = pendingMigrations.filter(
        (item) => item.migrationId !== migrationId,
      )
      setPendingMigrations(remaining)
      if (remaining.length > 0) return

      if (finishPausedForApproval && !deniedDuringApprovalRef.current) {
        setFinishPausedForApproval(false)
        toast.success('Connections and field changes applied')
        await completeFinish(true, 'approved')
        return
      }
      if (finishPausedForApproval) {
        setFinishPausedForApproval(false)
        toast.warning(
          'Mappings saved but some field type changes were not applied',
        )
        await completeFinish(true, 'denied')
        return
      }
      toast.success('Field change applied')
    } catch (err) {
      console.error('Approve migration failed', err)
      toast.error(
        err instanceof Error ? err.message : 'Unable to approve field change',
      )
    } finally {
      setIsResolvingMigration(false)
    }
  }

  const handleDenyMigration = async (migrationId: string) => {
    if (!onDenyMigration) {
      toast.error('Denial is unavailable')
      return
    }
    setIsResolvingMigration(true)
    try {
      await onDenyMigration(migrationId)
      deniedDuringApprovalRef.current = true
      const remaining = pendingMigrations.filter(
        (item) => item.migrationId !== migrationId,
      )
      setPendingMigrations(remaining)
      toast.warning(
        'Mappings saved but the field type change was not applied',
      )
      if (remaining.length > 0) return
      if (finishPausedForApproval) {
        setFinishPausedForApproval(false)
        await completeFinish(true, 'denied')
      }
    } catch (err) {
      console.error('Deny migration failed', err)
      toast.error(
        err instanceof Error ? err.message : 'Unable to deny field change',
      )
    } finally {
      setIsResolvingMigration(false)
    }
  }

  const handleFinish = async () => {
    setIsFinishing(true)
    try {
      let connectionsApplied = false
      if (showConnections && entityHandles.length > 0) {
        await persistStagedFieldMappings()
        const applyResult = await applyConnections(
          entityHandles,
          modelSelections,
          stagedDefaults,
        )
        connectionsApplied = true
        const incoming = mergePendingMigrations(applyResult?.pendingMigrations)
        if (incoming.length > 0 || pendingMigrations.length > 0) {
          deniedDuringApprovalRef.current = false
          setFinishPausedForApproval(true)
          return
        }
      }

      await completeFinish(connectionsApplied)
    } catch (err) {
      console.error('Finish setup failed', err)
      toast.error(
        err instanceof Error ? err.message : 'Unable to save setup',
      )
    } finally {
      setIsFinishing(false)
    }
  }

  if (!open) return null

  return (
    <div className={cn('flex h-full flex-col bg-background', className)}>
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {loadError}
          </div>
        ) : (
          <>
            {pendingMigrations.length > 0 && (
              <div className="space-y-3">
                {pendingMigrations.map((migration) => (
                  <MigrationApprovalAlert
                    key={migration.migrationId}
                    migrationId={migration.migrationId}
                    title="Field change needs approval"
                    description="Connections were saved. This CRM field type change is waiting for approval before it is applied."
                    impacts={migration.impacts}
                    timeoutMinutes={migration.timeoutMinutes}
                    createdAt={new Date(migration.createdAt)}
                    onApprove={(migrationId) => {
                      if (isResolvingMigration) return
                      void handleApproveMigration(migrationId)
                    }}
                    onDeny={(migrationId) => {
                      if (isResolvingMigration) return
                      void handleDenyMigration(migrationId)
                    }}
                  />
                ))}
              </div>
            )}
            {showConnections && (
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <Icon
                    name={connectionsReady ? 'Check' : 'Database'}
                    size={16}
                    className={
                      connectionsReady
                        ? 'text-emerald-600'
                        : 'text-muted-foreground'
                    }
                  />
                  <div>
                    <p className="text-sm font-medium">Connections</p>
                    <p className="text-xs text-muted-foreground">
                      {isRefreshingConnections
                        ? 'Updating connection preview…'
                        : summarizeConnections(analysis)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {analysis.entities.map((entity) => {
                    const modelId = modelSelections[entity.entityHandle]
                    const editorData = fullFieldEditorData[entity.entityHandle]
                    const isLoadingEditor = fullFieldEditorLoading[entity.entityHandle]
                    const isSavingEditor = fullFieldEditorSaving[entity.entityHandle]
                    
                    return (
                      <React.Fragment key={entity.entityHandle}>
                        <EntityConnectionRow
                          entity={entity}
                          models={models}
                          selectedModelId={modelId ?? null}
                          takenModelIds={takenModelIds}
                          canEditFields={canEditFields && !showFullFieldEditor}
                          expandedEntityPath={
                            fieldEditor?.entityHandle === entity.entityHandle
                              ? fieldEditor.entityPath
                              : null
                          }
                          expandedEditor={
                            fieldEditor?.entityHandle === entity.entityHandle
                              ? fieldEditorPanel
                              : null
                          }
                          onSelectModel={(nextModelId) =>
                            void handleModelChange(entity.entityHandle, nextModelId)
                          }
                          onOpenField={(target) =>
                            void openFieldEditor(entity, target)
                          }
                          defaultValues={stagedDefaults[entity.entityHandle] ?? {}}
                          onDefaultChange={(targetFieldId, value) => {
                            setStagedDefaults((prev) => ({
                              ...prev,
                              [entity.entityHandle]: {
                                ...(prev[entity.entityHandle] ?? {}),
                                [targetFieldId]: value,
                              },
                            }))
                          }}
                        />
                        {showFullFieldEditor && modelId && (
                          <FullFieldMappingPanel
                            entityHandle={entity.entityHandle}
                            entityLabel={entity.entityLabel}
                            isLoading={Boolean(isLoadingEditor)}
                            entityDef={editorData?.entityDef ?? null}
                            targetModelId={modelId}
                            fieldMappings={editorData?.fieldMappings ?? {}}
                            fields={editorData?.fields ?? []}
                            onMappingChange={(entityPath, mapping) =>
                              updateFullFieldMapping(entity.entityHandle, entityPath, mapping)
                            }
                            onSave={() => saveFullFieldEditor(entity.entityHandle)}
                            isSaving={Boolean(isSavingEditor)}
                          />
                        )}
                      </React.Fragment>
                    )
                  })}
                  {analysis.entities.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No entities to configure for this step.
                    </p>
                  )}
                </div>
              </section>
            )}

            {showLiveUpdates && (
              <section
                className={cn(
                  'space-y-3 rounded-xl border p-3',
                  enableLiveUpdates
                    ? 'border-foreground/15 bg-background'
                    : 'border-dashed border-muted-foreground/25 bg-muted/10',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={enableLiveUpdates ? 'Check' : 'Zap'}
                      size={16}
                      className={
                        enableLiveUpdates
                          ? 'text-emerald-600'
                          : 'text-muted-foreground'
                      }
                    />
                    <div>
                      <p className="text-sm font-medium">Live updates</p>
                      <p className="text-xs text-muted-foreground">
                        {requireLiveUpdates
                          ? 'Required to finish this setup step.'
                          : 'Wire sync workflows for incoming events.'}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant={
                      enableLiveUpdates || requireLiveUpdates
                        ? 'default'
                        : 'outline'
                    }
                    leftIcon={
                      enableLiveUpdates || requireLiveUpdates
                        ? 'Check'
                        : undefined
                    }
                    label={
                      enableLiveUpdates || requireLiveUpdates
                        ? 'Enabled'
                        : 'Disabled'
                    }
                    isDisabled={requireLiveUpdates}
                    handleClick={() => {
                      if (requireLiveUpdates) return
                      setEnableLiveUpdates((v) => !v)
                    }}
                  />
                </div>

                {enableLiveUpdates || requireLiveUpdates ? (
                  <div className="space-y-3">
                    {eventGroups.map((group) => {
                      const renderEventRow = (event: EntitySetupEventType) => {
                        const options = workflowOptions[event.type] ?? []
                        const value = selections[event.type] ?? null
                        const { items, appOptions } = buildWorkflowSelectItems(
                          options,
                          workflowHandles,
                          showOtherWorkflows,
                          true,
                        )
                        return (
                          <div
                            key={event.type}
                            className="space-y-1.5 rounded-md bg-muted/20 px-2.5 py-2"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <Icon
                                name={event.icon ?? 'Zap'}
                                size={13}
                                className="text-muted-foreground"
                              />
                              <p className="text-xs font-medium">
                                {event.label}
                              </p>
                              {event.badge && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px]"
                                  label={event.badge}
                                />
                              )}
                            </div>
                            {event.description && (
                              <p className="text-[11px] text-muted-foreground">
                                {event.description}
                              </p>
                            )}
                            <Select
                              id={`entity-setup-workflow-${event.type}`}
                              placeholder="Choose workflow"
                              value={value ?? NONE_VALUE}
                              items={items}
                              handleChange={(next) =>
                                setSelections((prev) => ({
                                  ...prev,
                                  [event.type]:
                                    next === NONE_VALUE ||
                                    next.startsWith('__group_')
                                      ? null
                                      : next,
                                }))
                              }
                            />
                            {appOptions.length === 0 && (
                              <p className="text-[11px] text-muted-foreground">
                                No type-compatible workflows found yet.
                              </p>
                            )}
                          </div>
                        )
                      }

                      return (
                        <div
                          key={group.entityKey}
                          className="space-y-2 rounded-lg border px-3 py-3"
                        >
                          <p className="text-sm font-medium">{group.label}</p>
                          {group.hasDirectionInfo ? (
                            <div className="space-y-2">
                              {group.inboundEvents.length > 0 && (
                                <>
                                  <DirectionSectionHeader direction="inbound" />
                                  {group.inboundEvents.map(renderEventRow)}
                                </>
                              )}
                              {group.outboundEvents.length > 0 && (
                                <>
                                  <DirectionSectionHeader direction="outbound" />
                                  {group.outboundEvents.map(renderEventRow)}
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {group.events.map(renderEventRow)}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {eventGroups.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No events to wire for this step.
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Live updates are disabled. Connections can still complete
                    this step.
                  </p>
                )}
              </section>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3">
        <Button
          label={finishLabel(mode, isFinishing)}
          handleClick={() => void handleFinish()}
          isLoading={isFinishing}
          isDisabled={
            isFinishing ||
            isLoading ||
            isRefreshingConnections ||
            isResolvingMigration ||
            pendingMigrations.length > 0 ||
            Boolean(loadError)
          }
        />
      </div>
    </div>
  )
}

export default EntitySetupDialog
